/**
 * Helpers for vehicle primary image + multi-image gallery.
 * Supports legacy docs that only have `image`, and new docs with `images` / `image_assets`.
 */

export const PLACEHOLDER_IMAGE_URL = '/no_image.jpg';

export function isPlaceholderImageUrl(url) {
  const path = String(url || '').split('?')[0].toLowerCase();
  return path === 'no_image.jpg' || path.endsWith('/no_image.jpg');
}

export function placeholderImageAsset() {
  return { url: PLACEHOLDER_IMAGE_URL, public_id: null };
}

export function realImageAssets(assets = []) {
  return (assets || []).filter((a) => {
    const url = typeof a === 'string' ? a : a?.url;
    return Boolean(url) && !isPlaceholderImageUrl(url);
  });
}

export function vehicleGallery(vehicle) {
  if (!vehicle) return [];
  if (Array.isArray(vehicle.images) && vehicle.images.length > 0) {
    return vehicle.images.filter(Boolean);
  }
  if (Array.isArray(vehicle.image_assets) && vehicle.image_assets.length > 0) {
    return vehicle.image_assets
      .map((a) => (typeof a === 'string' ? a : a?.url))
      .filter(Boolean);
  }
  return vehicle.image ? [vehicle.image] : [];
}

export function vehiclePrimaryImage(vehicle) {
  const gallery = vehicleGallery(vehicle);
  if (gallery.length) return gallery[0];
  return vehicle?.image || '';
}

export function vehicleUsesPlaceholder(vehicle) {
  if (!vehicle) return false;
  if (vehicle.images_pending) return true;
  return isPlaceholderImageUrl(vehiclePrimaryImage(vehicle));
}

export function assetsFromUploads(uploads = []) {
  return (uploads || [])
    .map((u) => ({
      url: u.url || u.secure_url || '',
      public_id: u.public_id || null,
    }))
    .filter((a) => a.url);
}

export function galleryFromAssets(assets = []) {
  return (assets || []).map((a) => a.url).filter(Boolean);
}

/**
 * Vehicle video gallery helpers (Cloudinary URLs).
 */
export function vehicleVideos(vehicle) {
  if (!vehicle) return [];
  if (Array.isArray(vehicle.video_assets) && vehicle.video_assets.length > 0) {
    return vehicle.video_assets
      .map((a) =>
        typeof a === 'string'
          ? { url: a, public_id: null, thumbnail_url: null }
          : {
              url: a?.url || a?.secure_url || '',
              public_id: a?.public_id || null,
              thumbnail_url: a?.thumbnail_url || null,
              duration: a?.duration,
              format: a?.format,
            }
      )
      .filter((a) => a.url);
  }
  if (Array.isArray(vehicle.videos) && vehicle.videos.length > 0) {
    return vehicle.videos
      .filter(Boolean)
      .map((url) => ({ url, public_id: null, thumbnail_url: null }));
  }
  return [];
}

export function videoAssetsFromVehicle(vehicle) {
  return vehicleVideos(vehicle);
}

export function videoAssetsFromUploads(uploads = []) {
  return (uploads || [])
    .map((u) => ({
      url: u.url || u.secure_url || '',
      public_id: u.public_id || null,
      thumbnail_url: u.thumbnail_url || null,
      format: u.format || null,
      duration: u.duration ?? null,
      bytes: u.bytes ?? null,
    }))
    .filter((a) => a.url);
}
